import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Row,
  Col,
  Divider,
  Button,
  Space,
  Typography,
  Switch,
  Alert,
} from "antd";
import {
  HistoryOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getTraceabilityTerminology,
  TraceabilityTerminology,
  IndustryPresetKey,
} from "./constants/traceabilityTerminology";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export interface UnifiedTraceableLogModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  instance?: any | null;
  instances?: any[];
  logTypes?: any[];
  referenceType?: string;
  referenceId?: number;
  terminologyPreset?: IndustryPresetKey | string;
  customTerminology?: Partial<TraceabilityTerminology>;
  onSubmit: (payload: {
    log: any;
    update_instance_status?: boolean;
    update_instance_metadata?: boolean;
    targetInstanceIds?: number[];
  }) => Promise<any>;
}

export const UnifiedTraceableLogModal: React.FC<UnifiedTraceableLogModalProps> = ({
  visible,
  onClose,
  onSuccess,
  instance,
  instances,
  logTypes,
  referenceType,
  referenceId,
  terminologyPreset,
  customTerminology,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const terminology = getTraceabilityTerminology(terminologyPreset, customTerminology);

  const isMultiple = Boolean(instances && instances.length > 0);
  const targetList = isMultiple ? instances! : instance ? [instance] : [];

  const availableTypes = logTypes && logTypes.length > 0 ? logTypes : terminology.defaultEventTypes;
  const selectedTypeVal = Form.useWatch("event_type_val", form);

  // Determine active type key
  const selectedTypeObj = availableTypes.find((lt) => lt.id === selectedTypeVal || lt.key === selectedTypeVal);
  const activeKey = selectedTypeObj?.key || (typeof selectedTypeVal === "string" ? selectedTypeVal : "OBSERVATION");

  useEffect(() => {
    if (visible) {
      form.resetFields();
      const defaultType = availableTypes[0];
      const initialWeight = instance?.metadata?.weight_kg;

      form.setFieldsValue({
        log_date: dayjs(),
        event_type_val: defaultType?.id || defaultType?.key || "VACCINATION",
        previous_status: instance?.status || "ACTIVE",
        new_status: instance?.status || "ACTIVE",
        update_instance_status: false,
        update_instance_metadata: true,
        metrics: {
          ...(initialWeight !== undefined ? { weight_kg: initialWeight } : {}),
        },
      });
    }
  }, [visible, instance, instances]);

  const handleSubmit = async () => {
    try {
      if (targetList.length === 0) return;
      const values = await form.validateFields();
      setLoading(true);

      const logTypeId = typeof values.event_type_val === "number" ? values.event_type_val : undefined;
      const selectedLabel = selectedTypeObj?.string_value || "Registro de Evento";

      const logPayload = {
        instance_id: isMultiple ? undefined : instance?.id,
        log_date: values.log_date ? values.log_date.toISOString() : new Date().toISOString(),
        log_type_option_id: logTypeId,
        title: values.title || selectedLabel,
        notes: values.notes || null,
        previous_status: instance?.status || "ACTIVE",
        new_status: values.update_instance_status ? values.new_status : null,
        metrics: values.metrics || {},
        ...(referenceType ? { reference_type: referenceType } : {}),
        ...(referenceId ? { reference_id: referenceId } : {}),
      };

      await onSubmit({
        log: logPayload,
        update_instance_status: values.update_instance_status,
        update_instance_metadata: values.update_instance_metadata,
        targetInstanceIds: isMultiple ? targetList.map((t) => t.id) : undefined,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error submitting traceable log:", err);
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = isMultiple
    ? `Registrar Evento para ${targetList.length} Elementos Seleccionados`
    : `Registrar Evento: ${instance?.identifier || "Elemento"}${instance?.name ? ` — ${instance.name}` : ""}`;

  return (
    <Modal
      open={visible}
      title={
        <Space align="center">
          <HistoryOutlined style={{ color: "#52c41a", fontSize: 20 }} />
          <span>{modalTitle}</span>
        </Space>
      }
      width={720}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {isMultiple ? `Guardar en ${targetList.length} Elementos` : "Guardar Evento"}
        </Button>,
      ]}
      destroyOnClose
    >
      {isMultiple && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Este evento se registrará en la hoja de vida de:{" "}
              <strong>
                {targetList
                  .slice(0, 5)
                  .map((t) => t.identifier || t.name)
                  .join(", ")}
                {targetList.length > 5 ? ` y ${targetList.length - 5} más...` : ""}
              </strong>
            </span>
          }
        />
      )}

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="event_type_val"
              label="Tipo de Evento / Categoría"
              rules={[{ required: true, message: "Seleccione el tipo de evento" }]}
            >
              <Select placeholder="Seleccionar tipo">
                {availableTypes.map((lt) => (
                  <Option key={lt.id || lt.key} value={lt.id || lt.key}>
                    {lt.string_value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item name="log_date" label="Fecha del Evento" rules={[{ required: true }]}>
              <DatePicker showTime style={{ width: "100%" }} format="YYYY-MM-DD HH:mm" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="title" label="Título o Resumen del Evento">
              <Input placeholder="Ej. Pesaje mensual, Vacuna Aftosa, Mantenimiento preventivo" />
            </Form.Item>
          </Col>
        </Row>

        {/* Dynamic Metric Inputs based on Log Type */}
        <Divider orientation="left" style={{ margin: "12px 0 16px" }}>
          <Text strong style={{ fontSize: 13, color: "#595959" }}>
            Datos y Métricas del Procedimiento
          </Text>
        </Divider>

        {activeKey === "MEASUREMENT" && (
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name={["metrics", "weight_kg"]} label="Peso Actual (kg)">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="Ej. 345.5" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metrics", "height_cm"]} label="Altura / Alzada (cm)">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="Ej. 135" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metrics", "temperature_c"]} label="Temperatura (°C)">
                <InputNumber style={{ width: "100%" }} placeholder="Ej. 38.5" />
              </Form.Item>
            </Col>
          </Row>
        )}

        {(activeKey === "VACCINATION" || activeKey === "TREATMENT") && (
          <>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name={["metrics", "medication_name"]} label="Medicamento / Vacuna">
                  <Input placeholder="Ej. Ivermectina, Aftosa" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={["metrics", "dose"]} label="Dosis Aplicada">
                  <Input placeholder="Ej. 5 ml, 2 tabletas" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={["metrics", "batch_number"]} label="Lote de Medicamento">
                  <Input placeholder="Ej. LOT-2026-X" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name={["metrics", "applied_by"]} label="Aplicado por (Operario)">
                  <Input placeholder="Nombre del profesional" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={["metrics", "withdrawal_days"]} label="Días de Retiro (Carne/Leche)">
                  <InputNumber style={{ width: "100%" }} min={0} placeholder="0" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={["metrics", "weight_kg"]} label="Peso (si se pesó)">
                  <InputNumber style={{ width: "100%" }} min={0} placeholder="kg" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {activeKey === "MAINTENANCE" && (
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name={["metrics", "operating_hours"]} label="Horas / Kilometraje">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="Ej. 1500" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metrics", "cost"]} label="Costo de Mantenimiento ($)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  prefix={<DollarOutlined />}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => (value ? value.replace(/\$\s?|(,*)/g, "") : "") as any}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={["metrics", "technician"]} label="Técnico / Proveedor">
                <Input placeholder="Taller / Técnico" />
              </Form.Item>
            </Col>
          </Row>
        )}

        {activeKey === "DEPRECIATION" && (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name={["metrics", "accumulated_depreciation"]} label="Depreciación Acumulada ($)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  prefix={<DollarOutlined />}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => (value ? value.replace(/\$\s?|(,*)/g, "") : "") as any}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={["metrics", "current_book_value"]} label="Valor en Libros Actual ($)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  prefix={<DollarOutlined />}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => (value ? value.replace(/\$\s?|(,*)/g, "") : "") as any}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="notes" label="Descripción / Observaciones">
              <TextArea rows={3} placeholder="Detalles adicionales del procedimiento, resultados, anomalías..." />
            </Form.Item>
          </Col>
        </Row>

        {!isMultiple && (
          <>
            <Divider orientation="left" style={{ margin: "12px 0 16px" }}>
              <Text strong style={{ fontSize: 13, color: "#595959" }}>
                Transición de Estado
              </Text>
            </Divider>

            <Row gutter={16} align="middle">
              <Col xs={24} md={12}>
                <Form.Item
                  name="update_instance_status"
                  label="¿Actualizar el estado del elemento con este evento?"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Sí" unCheckedChildren="No" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="new_status" label="Nuevo Estado del Elemento">
                  <Select>
                    <Option value="ACTIVE">Activo / Disponible</Option>
                    <Option value="IN_PRODUCTION">En Producción / Ciclo</Option>
                    <Option value="IN_USE">En Uso / Asignado</Option>
                    <Option value="MAINTENANCE">En Mantenimiento</Option>
                    <Option value="SOLD">Vendido</Option>
                    <Option value="DISPOSED">Baja / Descarte</Option>
                    <Option value="RETIRED">Retirado</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <Form.Item name="update_instance_metadata" valuePropName="checked" initialValue={true} hidden>
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UnifiedTraceableLogModal;
